import { StandardEvents } from '@shopify/events';

const SEARCH_DELAY = 300;
const DRAWER_OPEN_EVENT = 'theme-drawer:open';

class EnquiryDrawer extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.product-enquiry__form');
    this.productsElement = this.querySelector('[data-enquiry-products]');
    this.picker = this.querySelector('[data-product-picker]');
    this.searchInput = this.querySelector('[data-product-search]');
    this.searchResults = this.querySelector('[data-search-results]');
    this.optionPicker = this.querySelector('[data-option-picker]');
    this.confirmButton = this.querySelector('[data-confirm-product]');
    this.status = this.querySelector('[data-form-status]');
    this.items = [this.initialItem()];
    this.pendingProduct = null;
    this.searchController = null;
    this.searchTimer = null;
    this.searchRequestId = 0;
    this.variantChangeGeneration = 0;
    this.searchResultClickHandler = (event) => this.selectSearchResult(event);
    this.productSelectHandler = (event) => this.handleProductSelect(event);
    this.drawerOpenHandler = () => this.syncInitialItemFromPicker();
    this.searchResultsListening = false;
    this.renderItems();

    this.productSection = this.closest('.shopify-section, dialog');
    this.drawer = this.querySelector('theme-drawer');
    this.productSection?.addEventListener(StandardEvents.productSelect, this.productSelectHandler);
    this.drawer?.addEventListener(DRAWER_OPEN_EVENT, this.drawerOpenHandler);

    this.querySelector('[data-add-product]')?.addEventListener('click', () => this.openPicker());
    this.querySelector('[data-cancel-product]')?.addEventListener('click', () => this.closePicker());
    this.confirmButton?.addEventListener('click', () => this.confirmProduct());
    this.searchInput?.addEventListener('input', () => this.queueSearch());
    this.optionPicker?.addEventListener('change', () => this.resolveVariant());
    this.productsElement?.addEventListener('click', (event) => this.deleteProduct(event));
    this.form?.addEventListener('submit', (event) => this.submit(event));
  }

  disconnectedCallback() {
    this.productSection?.removeEventListener(StandardEvents.productSelect, this.productSelectHandler);
    this.drawer?.removeEventListener(DRAWER_OPEN_EVENT, this.drawerOpenHandler);
    this.cancelSearch();
    this.disableSearchResultClicks();
  }

  initialItem() {
    return this.itemFromElement(this);
  }

  itemFromElement(element) {
    const title = element.dataset.productTitle || '';
    const variantTitle = element.dataset.variantTitle === 'Default Title' ? '' : element.dataset.variantTitle || '';
    let initialOptions = { names: [], values: [] };

    try {
      initialOptions = JSON.parse(element.querySelector('[data-initial-options]')?.textContent || '{"names":[],"values":[]}');
    } catch {
      // Keep the product available in the enquiry even if its option data is malformed.
    }

    const options = {};
    initialOptions.names.forEach((name, index) => {
      if (initialOptions.values[index] && initialOptions.values[index] !== 'Default Title') {
        options[name] = initialOptions.values[index];
      }
    });

    return {
      productId: element.dataset.productId || '',
      productHandle: element.dataset.productHandle || '',
      productTitle: title,
      variantId: element.dataset.variantId || '',
      variantTitle,
      options,
      url: element.dataset.productUrl || '',
      initial: true
    };
  }

  updateInitialItem({ productTitle, variantId, variantTitle, selectedOptions, sourceItem } = {}) {
    const currentItem = this.items.find((item) => item.initial);
    if (!currentItem) return;

    if (sourceItem) {
      Object.assign(currentItem, sourceItem, { initial: true });
    } else {
      if (productTitle) currentItem.productTitle = productTitle;
      if (variantId !== undefined) currentItem.variantId = this.normalizeShopifyId(variantId);

      const options = { ...currentItem.options };
      (selectedOptions || []).forEach(({ name, value }) => {
        if (name && value && value !== 'Default Title') options[name] = value;
      });
      currentItem.options = options;
      currentItem.variantTitle = variantTitle === 'Default Title'
        ? ''
        : variantTitle || Object.values(options).join(' / ');
    }

    this.dataset.productTitle = currentItem.productTitle;
    this.dataset.variantId = String(currentItem.variantId);
    this.dataset.variantTitle = currentItem.variantTitle;
    this.renderItems();
  }

  syncInitialItemFromPicker() {
    const picker = [...(this.productSection?.querySelectorAll('variant-picker') || [])]
      .find((element) => this.normalizeShopifyId(element.dataset.productId) === this.normalizeShopifyId(this.dataset.productId));
    if (!picker || typeof picker.getAllSelectedOptions !== 'function') return;

    const selectedOptions = picker.getAllSelectedOptions();
    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    const selectedVariantId = picker.selectedOption?.dataset.variantId;
    this.updateInitialItem({ selectedOptions, variantId: urlVariantId || selectedVariantId });
  }

  handleProductSelect(event) {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (this.normalizeShopifyId(event.product?.id) !== this.normalizeShopifyId(this.dataset.productId)) return;

    const generation = ++this.variantChangeGeneration;
    const selectedOptions = event.selectedOptions || [];

    // Update the visible colour immediately; the resolved response below supplies
    // the authoritative variant ID/title and protects fast consecutive changes.
    this.updateInitialItem({
      productTitle: event.product?.title,
      variantId: event.detail?.variantId,
      selectedOptions
    });

    event.promise
      .then(({ variant, detail }) => {
        if (generation !== this.variantChangeGeneration) return;

        const sourceElement = [...(detail?.html?.querySelectorAll('enquiry-drawer') || [])]
          .find((element) => this.normalizeShopifyId(element.dataset.productId) === this.normalizeShopifyId(this.dataset.productId));

        if (sourceElement) {
          this.updateInitialItem({ sourceItem: this.itemFromElement(sourceElement) });
          return;
        }

        this.updateInitialItem({
          variantId: detail?.resource?.id || variant?.id,
          variantTitle: detail?.resource?.title || variant?.title,
          selectedOptions: variant?.selectedOptions || selectedOptions
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-enquiry] Variant update failed:', error);
      });
  }

  renderItems() {
    this.productsElement.innerHTML = this.items.map((item, index) => {
      const options = Object.entries(item.options || {})
        .map(([name, value]) => `<p><span>${this.escape(name)}:</span> <span>${this.escape(value)}</span></p>`)
        .join('');

      const remove = item.initial
        ? ''
        : `<button type="button" class="button-unstyled product-enquiry__delete" data-delete-index="${index}">${this.escape(this.dataset.deleteLabel)}</button>`;

      return `
        <article class="product-enquiry__product">
          <div class="product-enquiry__product-copy">
            <p><span>${this.escape(this.dataset.productLabel)}:</span> <span>${this.escape(item.productTitle)}</span></p>
            ${options}
          </div>
          ${remove}
        </article>
      `.trim();
    }).join('');
  }

  openPicker() {
    this.cancelSearch();
    this.disableSearchResultClicks();
    this.picker.hidden = false;
    this.pendingProduct = null;
    this.searchResults.textContent = '';
    this.optionPicker.hidden = true;
    this.confirmButton.disabled = true;
    this.searchInput.value = '';
    this.searchInput.focus();
  }

  closePicker() {
    this.picker.hidden = true;
    this.pendingProduct = null;
    this.cancelSearch();
    this.disableSearchResultClicks();
  }

  enableSearchResultClicks() {
    if (!this.searchResults || this.searchResultsListening) return;
    this.searchResults.addEventListener('click', this.searchResultClickHandler);
    this.searchResultsListening = true;
  }

  disableSearchResultClicks() {
    if (!this.searchResults || !this.searchResultsListening) return;
    this.searchResults.removeEventListener('click', this.searchResultClickHandler);
    this.searchResultsListening = false;
  }

  cancelSearch() {
    clearTimeout(this.searchTimer);
    this.searchTimer = null;
    this.searchRequestId += 1;
    this.searchController?.abort();
    this.searchController = null;
  }

  queueSearch() {
    clearTimeout(this.searchTimer);
    const query = this.searchInput.value.trim();
    if (query.length < 2) {
      this.cancelSearch();
      this.searchResults.textContent = '';
      return;
    }
    this.searchTimer = setTimeout(() => this.search(query), SEARCH_DELAY);
  }

  async search(query) {
    this.searchController?.abort();
    this.searchController = new AbortController();
    const requestId = ++this.searchRequestId;
    this.searchResults.textContent = this.dataset.searchingLabel;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=show`;
      const response = await fetch(url, { signal: this.searchController.signal });
      if (!response.ok) throw new Error('Search failed');

      const json = await response.json();
      const candidates = json.resources?.results?.products || [];
      const eligible = (await Promise.all(candidates.map((product) => this.isEligible(product)))).filter(Boolean);

      if (requestId !== this.searchRequestId || this.picker.hidden) return;
      this.renderSearchResults(eligible);
    } catch (error) {
      if (error.name !== 'AbortError' && requestId === this.searchRequestId) {
        this.searchResults.textContent = this.dataset.searchErrorLabel;
      }
    }
  }

  async isEligible(product) {
    try {
      const response = await fetch(product.url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) return null;

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const marker = documentFragment.querySelector(`[data-enquiry-product-marker="true"][data-enquiry-product-id="${product.id}"]`);
      return marker ? product : null;
    } catch {
      return null;
    }
  }

  renderSearchResults(products) {
    this.disableSearchResultClicks();
    if (!products.length) {
      this.searchResults.textContent = this.dataset.noResultsLabel;
      return;
    }

    this.searchResults.innerHTML = products
      .map((product) => `<button type="button" class="button-unstyled product-enquiry__search-result" data-product-handle="${this.escape(product.handle)}">${this.escape(product.title)}</button>`)
      .join('');
    this.enableSearchResultClicks();
  }

  async selectSearchResult(event) {
    const button = event.target.closest('[data-product-handle]');
    if (!button) return;

    const productHandle = button.dataset.productHandle;
    this.cancelSearch();
    this.disableSearchResultClicks();
    button.disabled = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(productHandle)}.js`);
      if (!response.ok) throw new Error('Product failed');

      this.pendingProduct = await response.json();
      this.renderOptions();
    } catch {
      this.searchResults.textContent = this.dataset.searchErrorLabel;
    }
  }

  renderOptions() {
    const product = this.pendingProduct;
    this.searchResults.textContent = product.title;
    this.pendingOptionDefinitions = (product.options || [])
      .map((option, index) => ({ ...option, variantIndex: index }))
      .filter((option) => !(option.name === 'Title' && option.values?.length === 1 && option.values[0] === 'Default Title'));

    const options = this.pendingOptionDefinitions.map((option) => {
      const index = option.variantIndex;
      const values = [...new Set(product.variants.map((variant) => variant.options[index]))];
      return `<label>${this.escape(option.name)}<select class="product-enquiry__input product-enquiry__select" data-option-index="${index}"><option value="">${this.escape(this.dataset.selectOptionsLabel)}</option>${values.map((value) => `<option value="${this.escape(value)}">${this.escape(value)}</option>`).join('')}</select></label>`;
    });

    this.optionPicker.innerHTML = options.join('');
    this.optionPicker.hidden = false;
    this.confirmButton.disabled = this.pendingOptionDefinitions.length > 0;
    if (!this.pendingOptionDefinitions.length) this.resolveVariant();
  }

  resolveVariant() {
    if (!this.pendingProduct) return;
    const selects = [...this.optionPicker.querySelectorAll('select')];
    if (selects.some((select) => !select.value)) {
      this.confirmButton.disabled = true;
      return;
    }

    const selected = new Map(selects.map((select) => [Number(select.dataset.optionIndex), select.value]));
    this.pendingVariant = this.pendingProduct.variants.find((variant) =>
      [...selected].every(([index, value]) => variant.options[index] === value)
    );
    this.confirmButton.disabled = !this.pendingVariant;
  }

  confirmProduct() {
    if (!this.pendingProduct || !this.pendingVariant) return;
    if (this.items.some((item) => String(item.variantId) === String(this.pendingVariant.id))) {
      this.showStatus(this.dataset.duplicateLabel);
      return;
    }

    const options = {};
    (this.pendingOptionDefinitions || []).forEach((option) => {
      options[option.name] = this.pendingVariant.options[option.variantIndex];
    });

    this.items.push({
      productId: String(this.pendingProduct.id),
      productHandle: this.pendingProduct.handle,
      productTitle: this.pendingProduct.title,
      variantId: String(this.pendingVariant.id),
      variantTitle: this.pendingVariant.title,
      options,
      url: this.pendingProduct.url,
      initial: false
    });

    this.renderItems();
    this.closePicker();
  }

  deleteProduct(event) {
    const button = event.target.closest('[data-delete-index]');
    if (!button) return;
    const index = Number(button.dataset.deleteIndex);
    if (this.items[index]?.initial) return;

    this.items.splice(index, 1);
    this.renderItems();
  }

  getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : '';
  }

  async submit(event) {
    event.preventDefault();
    if (!this.form.reportValidity()) return;

    this.syncInitialItemFromPicker();
    const submitButton = this.form.querySelector('[type="submit"]');
    submitButton.disabled = true;
    this.showStatus('');

    const datajetUrl = this.dataset.datajetUrl;
    if (!datajetUrl) {
      this.showStatus(this.dataset.configErrorLabel);
      submitButton.disabled = false;
      return;
    }

    const data = new FormData(this.form);
    const idempotencyKey = crypto.randomUUID();

    const sourceItem = this.items.find((item) => item.initial) || this.items[0];
    const payload = {
      firstname: data.get('firstname') || '',
      lastname: data.get('lastname') || '',
      email: data.get('email') || '',
      phone: data.get('mobilephone') || data.get('phone') || '',
      mobilephone: data.get('mobilephone') || data.get('phone') || '',
      state: data.get('state_dropdown') || data.get('state') || '',
      state_dropdown: data.get('state_dropdown') || data.get('state') || '',
      message: data.get('message') || '',
      gdpr_consent: true,
      idempotency_key: idempotencyKey,
      hutk: this.getCookie('hubspotutk'),
      source_product: {
        id: String(sourceItem.productId),
        handle: sourceItem.productHandle,
        title: sourceItem.productTitle,
        url: sourceItem.url,
        variant_id: String(sourceItem.variantId),
        variant_title: sourceItem.variantTitle
      },
      selected_products: this.items.map((item) => ({
        id: String(item.productId),
        handle: item.productHandle,
        title: item.productTitle,
        url: item.url,
        variant_id: String(item.variantId),
        variant_title: item.variantTitle,
        options: item.options
      })),
      page_url: location.href,
      page_title: document.title
    };

    try {
      const response = await fetch(datajetUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': idempotencyKey
        },
        body: JSON.stringify(payload)
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok || json.success === false) {
        if (json.errors) {
          const firstError = Object.values(json.errors).find(Boolean);
          this.showStatus(firstError || this.dataset.submitErrorLabel);
        } else {
          this.showStatus(this.dataset.submitErrorLabel);
        }
        submitButton.disabled = false;
        return;
      }
      this.showConfirmation();
    } catch {
      this.showStatus(this.dataset.submitErrorLabel);
      submitButton.disabled = false;
    }
  }

  showConfirmation() {
    this.form.hidden = true;
    const confirmation = this.querySelector('[data-confirmation]');
    if (confirmation) {
      confirmation.hidden = false;
      confirmation.focus();
    }
  }

  showStatus(message) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = !message;
  }

  normalizeShopifyId(value) {
    return String(value || '').split('/').pop();
  }

  escape(value) {
    const span = document.createElement('span');
    span.textContent = String(value ?? '');
    return span.innerHTML;
  }
}

if (!customElements.get('enquiry-drawer')) {
  customElements.define('enquiry-drawer', EnquiryDrawer);
}
